import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  RowSelectionState,
  Row,
} from '@tanstack/react-table';
import { useState, useEffect } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  pageSize?: number;
  totalCount?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  onRowSelectionChange?: (rows: Row<T>[]) => void;
  getRowId?: (row: T) => string;
}

export default function DataTable<T>({
  data,
  columns,
  pageSize = 20,
  totalCount,
  page = 1,
  onPageChange,
  isLoading = false,
  onRowClick,
  onRowSelectionChange,
  getRowId,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: onPageChange ? undefined : getPaginationRowModel(),
    manualPagination: !!onPageChange,
    enableRowSelection: true,
    getRowId: getRowId,
  });

  // Notify parent of selection changes
  useEffect(() => {
    if (onRowSelectionChange) {
      const selectedRows = table.getSelectedRowModel().rows;
      onRowSelectionChange(selectedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 1;

  return (
    <div className="w-full">
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <button
                        type="button"
                        className="flex items-center gap-2 cursor-pointer select-none hover:text-gray-700"
                        onClick={header.column.getToggleSortingHandler()}
                        aria-label="Sort column"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span className="text-gray-400">
                          {{
                            asc: <ChevronUpIcon className="w-4 h-4" />,
                            desc: <ChevronDownIcon className="w-4 h-4" />,
                          }[header.column.getIsSorted() as string] ?? (
                            <span className="w-4 h-4 opacity-0 group-hover:opacity-50">
                              <ChevronUpIcon className="w-4 h-4" />
                            </span>
                          )}
                        </span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              // False positive from jsx-a11y/control-has-associated-label on table row/cell.
              // These are non-interactive loading placeholders.
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <tr>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-500">
                    <div className="w-5 h-5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                  No data available
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`hover:bg-gray-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(totalCount !== undefined || !onPageChange) && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <div className="text-sm text-gray-700">
            Showing{' '}
            <span className="font-medium">
              {onPageChange ? (page - 1) * pageSize + 1 : table.getState().pagination.pageIndex * pageSize + 1}
            </span>{' '}
            to{' '}
            <span className="font-medium">
              {Math.min(
                onPageChange
                  ? page * pageSize
                  : (table.getState().pagination.pageIndex + 1) * pageSize,
                totalCount || data.length
              )}
            </span>{' '}
            of <span className="font-medium">{totalCount || data.length}</span> results
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange ? onPageChange(page - 1) : table.previousPage()}
              disabled={onPageChange ? page <= 1 : !table.getCanPreviousPage()}
              className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Go to previous page"
            >
              <span className="sr-only">Previous page</span>
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-700">
              Page {onPageChange ? page : table.getState().pagination.pageIndex + 1} of{' '}
              {onPageChange ? totalPages : table.getPageCount()}
            </span>
            <button
              onClick={() => onPageChange ? onPageChange(page + 1) : table.nextPage()}
              disabled={onPageChange ? page >= totalPages : !table.getCanNextPage()}
              className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Go to next page"
            >
              <span className="sr-only">Next page</span>
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
